import React, { useEffect, useMemo, useState } from "react";
import {
  Backdrop,
  CircularProgress,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  TextField,
  Switch,
  FormControl,
  FormLabel,
  IconButton,
  Button,
  Box,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DataTable from "comps/Tables/DataTable";
import TLinkApi from "services/tlink.api";
import MDButton from "components/MDButton";
import MRTClientDataTable from "comps/Tables/DataTable/MRTClientDataTable";
import { MRT_ShowHideColumnsButton, MRT_ToggleFiltersButton, MRT_ToggleFullScreenButton } from "material-react-table";

const BASE_ENDPOINT = "/configurations/supplier-fill-rate";

const FillRateConfig = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [data, setData] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState("add");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [supplierId, setSupplierId] = useState("");
  const [isExcluded, setIsExcluded] = useState(false);

  const [originalSupplierId, setOriginalSupplierId] = useState("");
  const [id, setId] = useState("");

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await TLinkApi.get(BASE_ENDPOINT);
      const rows = (res?.data ?? []).map((r) => ({
        id: r.id,
        supplier_id: r.supplier_id,
        supplier_name: r.supplier_name,
        is_excluded: !!r.is_excluded,
        created_at: r.created_at,
        updated_at: r.updated_at,
      }));
      setData(rows);
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const existingIds = useMemo(() => new Set(data.map(d => String(d.supplier_id))), [data]);

  const openAdd = () => {
    setMode("add");
    setSupplierId("");
    setIsExcluded(false);
    setOriginalSupplierId("");
    setId("");
    setErrorMsg("");
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setMode("edit");
    setSupplierId(String(row.supplier_id ?? ""));
    setIsExcluded(Boolean(row.is_excluded));
    setOriginalSupplierId(String(row.supplier_id ?? ""));
    setId(row.id ?? "");
    setErrorMsg("");
    setModalOpen(true);
  };

  const valid = supplierId.trim() !== "";

  const handleSave = async () => {
    if (!valid) {
      setErrorMsg("Supplier ID is required.");
      return;
    }
    const newId = String(Number(supplierId));
    const oldId = String(Number(originalSupplierId));
    if (mode === "add" && existingIds.has(newId)) {
      setErrorMsg("This supplier already has a fill-rate rule.");
      return;
    }
    if (mode === "edit" && newId !== oldId && existingIds.has(newId)) {
      setErrorMsg("Another record already uses this Supplier ID.");
      return;
    }

    setSubmitting(true);
    setErrorMsg("");
    try {
      const payload = {
        supplierId: Number(supplierId),
        isExcluded: Boolean(isExcluded),
      };

      if (mode === "add") {
        await TLinkApi.post(BASE_ENDPOINT, payload);
      } else {
        payload.previousSupplierId = Number(originalSupplierId);
        await TLinkApi.update(`${BASE_ENDPOINT}/${id}`, payload);
      }

      setModalOpen(false);
      await fetchData();
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Something went wrong";
      setErrorMsg(msg);
    } finally {
      setSubmitting(false);
    }
  };
  const oneLine = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };

  const columns = useMemo(
    () => [
      {
        accessor: "supplier_id",
        Header: "Supplier Id",
        align: "left",
        Cell: ({ row }) => {
          const v = row?.original?.supplier_id;
          return <div>{(v === null || v === undefined || v === "") ? "-" : v}</div>;
        },
      },
      {
        accessor: "supplier_name",
        Header: "Supplier Name",
        align: "left",
        sortable: true,
        Cell: ({ row }) => {
          const name = row?.original?.supplier_name || "";
          const display = name ? (name.length > 20 ? name.slice(0, 20) + "..." : name) : "-";
          return (
            <Tooltip title={name && name.length > 20 ? name : ""}>
              <div style={oneLine}>{display}</div>
            </Tooltip>
          );
        },
      },
      {
        accessor: "is_excluded",
        Header: "Excluded",
        align: "center",
        headerAlign: "center",
        Cell: ({ row }) => (row.original.is_excluded ? "Yes" : "No"),
      },
      {
        accessor: "actions",
        Header: "Actions",
        align: "right",
        headerAlign: "right",
        Cell: ({ row }) => (
          <Stack direction="row" spacing={1} justifyContent="flex-end" pr={1}>
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => openEdit(row.original)} sx={{ p: 0.5 }}>
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    []
  );

  const [fullScr, setFullScr] = useState(false);
  const renderToolbarInternalActions = ({ table }) => {
    return (
      <>
        <div style={{ display: "flex", alignItems: "center", gap: "0rem" }}>
          <Button variant="contained" color="info" onClick={openAdd}>
            + New
          </Button>
          <MRT_ShowHideColumnsButton table={table} />
          <MRT_ToggleFiltersButton table={table} />
          <div onClick={() => setFullScr(!fullScr)} >
            <MRT_ToggleFullScreenButton table={table} />
          </div>
        </div>
      </>
    );
  };

  return (
    <div>
      {isLoading ? (
        <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isLoading}>
          <CircularProgress color="inherit" />
        </Backdrop>
      ) : null}

      {/* <DataTable
        table={{ columns, rows: data }}
        isSorted
        entriesPerPage={entriesPerPage}
        canSearch={false}
        tablePage={page}
        setTablePage={setPage}
        setEntriesPerPage={setEntriesPerPage}
        totalLength={data.length}
        pagination
        enableTopToolbar
        tableMaxHeight={data.length === 0 ? "calc(100vh - 220px)" : "calc(100vh - 220px)"}
        tableMinHeight={data.length === 0 ? "calc(100vh - 220px)" : "calc(100vh - 220px)"}
        renderTopToolbarCustomActions={() => (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: window.innerWidth > 1600 ? "calc(76vw - 580px)" : "calc(76vw - 565px)", marginTop: "3px" }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Fill Rate Configuration
            </Typography>
            <Button variant="contained" color="info" onClick={openAdd} sx={{ ml: 2 }}>
              + New
            </Button>
          </Box>
        )}
      /> */}

      <MRTClientDataTable
        table={{ columns, rows: data }}
        isSorted
        canSearch={false}
        enableTopToolbar
        defaultPageSize={entriesPerPage}
        entriesOption={["10", "20", "30", "40", "50"]}
        tableMaxHeight="calc(100vh - 220px)"
        tableMinHeight="calc(100vh - 220px)"
        renderToolbarInternalActions={renderToolbarInternalActions}
        fullScr={fullScr}
        renderTopToolbarCustomActions={() => (
          <Box
          // sx={{
          //   display: "flex",
          //   justifyContent: "space-between",
          //   alignItems: "center",
          //   width:
          //     window.innerWidth > 1600
          //       ? "calc(76vw - 580px)"
          //       : "calc(76vw - 565px)",
          //   mt: "3px",
          // }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Fill Rate Configuration
            </Typography>
            {/* <Button variant="contained" color="info" onClick={openAdd} sx={{ ml: 2 }}>
              + New
            </Button> */}
          </Box>
        )}
      />

      <Dialog open={modalOpen} onClose={() => !submitting && setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{mode === "add" ? "Add" : "Update"}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <FormControl fullWidth>
              <FormLabel sx={{ mb: 0.5, fontSize: 12 }}>Supplier ID</FormLabel>
              <TextField
                size="small"
                type="number"
                placeholder="Enter supplier ID"
                value={supplierId}
                onChange={(e) => setSupplierId(e.target.value)}
              />
            </FormControl>

            <FormControl>
              <FormLabel sx={{ mb: 0.5, fontSize: 12 }}>Excluded</FormLabel>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Switch checked={isExcluded} onChange={(e) => setIsExcluded(e.target.checked)} />
                <Typography variant="body2">{isExcluded ? "Yes" : "No"}</Typography>
              </Stack>
            </FormControl>

            {errorMsg ? (
              <Typography variant="body2" color="error">
                {errorMsg}
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <MDButton variant="outlined" color="secondary" onClick={() => setModalOpen(false)} disabled={submitting}>
            Cancel
          </MDButton>
          <MDButton
            variant="contained"
            color="info"
            onClick={handleSave}
            disabled={submitting || !valid}
          >
            {submitting ? (mode === "add" ? "Saving…" : "Updating…") : mode === "add" ? "Save" : "Update"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FillRateConfig;